import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Icon from '../../../base/icons/components/Icon';
import { IconCloseLarge, IconSidePanelDock } from '../../../base/icons/svg';
import { bringFloatingSidePanelToFront, closeSidePanelTab, dockSidePanelTab, setFloatingSidePanelGeometry } from '../../actions';
import { getSidePanelState } from '../../functions';
import { SIDE_PANEL_TAB_META } from '../../tabMeta';
import logger from '../../logger';
import SidePanelTabContent from './SidePanelTabContent';
const useStyles = makeStyles()(theme => {
    return {
        window: {
            backgroundColor: theme.palette.ui01,
            border: `1px solid ${theme.palette.ui04}`,
            borderRadius: '10px',
            boxShadow: '0 8px 28px rgba(0, 0, 0, 0.28)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'fixed'
        },
        titleBar: {
            alignItems: 'center',
            backgroundColor: theme.palette.ui02,
            borderBottom: `1px solid ${theme.palette.ui04}`,
            boxSizing: 'border-box',
            cursor: 'move',
            display: 'flex',
            flexShrink: 0,
            gap: theme.spacing(2),
            height: '44px',
            justifyContent: 'space-between',
            padding: `0 ${theme.spacing(3)}`,
            userSelect: 'none'
        },
        title: {
            ...theme.typography.bodyShortBold,
            alignItems: 'center',
            color: theme.palette.text01,
            display: 'flex',
            gap: '8px',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        actions: {
            alignItems: 'center',
            display: 'flex',
            flexShrink: 0,
            gap: theme.spacing(1)
        },
        actionButton: {
            alignItems: 'center',
            background: 'transparent',
            border: 0,
            borderRadius: '6px',
            color: theme.palette.icon01,
            cursor: 'pointer',
            display: 'flex',
            height: '28px',
            justifyContent: 'center',
            padding: 0,
            width: '28px',
            '&:hover': {
                backgroundColor: theme.palette.ui03
            }
        },
        body: {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden'
        }
    };
});
const TAB_META = SIDE_PANEL_TAB_META;
/**
 * Draggable in-meeting floating window for a popped-out side panel app.
 *
 * @param {IProps} props - Props.
 * @returns {JSX.Element | null}
 */
export default function SidePanelFloatingWindow({ tabId }) {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const geometry = useSelector((state) => getSidePanelState(state).floatingGeometry[tabId]);
    const clientSize = useSelector((state) => state['features/base/responsive-ui']);
    const dragRef = useRef(null);
    const meta = TAB_META[tabId];
    const onBringToFront = useCallback(() => {
        dispatch(bringFloatingSidePanelToFront(tabId));
    }, [dispatch, tabId]);
    const onDock = useCallback(() => {
        logger.info('收回浮窗到侧栏', { tabId });
        dispatch(dockSidePanelTab(tabId));
    }, [dispatch, tabId]);
    const onClose = useCallback(() => {
        logger.info('关闭浮窗', { tabId });
        dispatch(closeSidePanelTab(tabId));
    }, [dispatch, tabId]);
    const onPointerDown = useCallback((event) => {
        if (!geometry) {
            return;
        }
        const target = event.target;
        if (target.closest('button')) {
            return;
        }
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = {
            originX: geometry.x,
            originY: geometry.y,
            startX: event.clientX,
            startY: event.clientY
        };
        dispatch(bringFloatingSidePanelToFront(tabId));
    }, [dispatch, geometry, tabId]);
    const onPointerMove = useCallback((event) => {
        if (!dragRef.current || !geometry) {
            return;
        }
        const dx = event.clientX - dragRef.current.startX;
        const dy = event.clientY - dragRef.current.startY;
        const maxX = Math.max(0, (clientSize.clientWidth || window.innerWidth) - geometry.width);
        const maxY = Math.max(0, (clientSize.clientHeight || window.innerHeight) - 48);
        const x = Math.min(Math.max(0, dragRef.current.originX + dx), maxX);
        const y = Math.min(Math.max(0, dragRef.current.originY + dy), maxY);
        dispatch(setFloatingSidePanelGeometry(tabId, { x, y }));
    }, [clientSize.clientHeight, clientSize.clientWidth, dispatch, geometry, tabId]);
    const onPointerUp = useCallback(() => {
        dragRef.current = null;
    }, []);
    if (!geometry) {
        return null;
    }
    return (React.createElement("div", { className: classes.window, onMouseDown: onBringToFront, style: {
            height: geometry.height,
            left: geometry.x,
            top: geometry.y,
            width: geometry.width,
            zIndex: geometry.zIndex
        } },
        React.createElement("div", { className: classes.titleBar, onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: onPointerUp },
            React.createElement("div", { className: classes.title },
                React.createElement(Icon, { size: 16, src: meta.icon }),
                React.createElement("span", null, t(meta.labelKey))),
            React.createElement("div", { className: classes.actions },
                React.createElement("button", { "aria-label": t('sidePanel.dock'), className: classes.actionButton, onClick: onDock, title: t('sidePanel.dock'), type: 'button' },
                    React.createElement(Icon, { size: 18, src: IconSidePanelDock })),
                React.createElement("button", { "aria-label": t('sidePanel.closeTab'), className: classes.actionButton, onClick: onClose, title: t('sidePanel.closeTab'), type: 'button' },
                    React.createElement(Icon, { size: 18, src: IconCloseLarge })))),
        React.createElement("div", { className: classes.body },
            React.createElement(SidePanelTabContent, { surface: 'floating', tabId: tabId }))));
}
